import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { BackArrow, CreateBtn, DeleteBtn } from 'lib/styles/icons';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import DeviceInsertModal from './DeviceInsertModal';
import AskModal from 'components/common/AskModal';
import { deleteDevice } from 'lib/api/device';
import useLoginCheck from 'hooks/common/useLoginCheck';
import NotiModal from 'components/common/NotiModal';

const DeviceRegisterBoxItem = ({ device }) => {
  const { id } = useParams();
  const queryClient = useQueryClient();

  const [showModal, setShowModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const deleteDeviceMutation = useMutation(deleteDevice, {
    onError: error => {
      if (error.response.statue === 500) {
        setShowErrorModal(true);
        setShowModal(false);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries('device');
      setShowModal(false);
    },
  });
  useLoginCheck(deleteDeviceMutation.error?.response, () => {
    deleteDeviceMutation.mutate({ id, deviceId: device.id });
  });

  return (
    <>
      {showErrorModal && (
        <NotiModal
          closeModal={() => setShowErrorModal(false)}
          msg="서버 오류로 삭제에 실패했습니다"
          fontL
        />
      )}
      {showModal && (
        <AskModal
          closeModal={() => setShowModal(false)}
          action="삭제"
          message="정말 삭제하시겠습니까?"
          actionFc={() =>
            deleteDeviceMutation.mutate({ id, deviceId: device.id })
          }
        />
      )}
      <DeviceRegisterBoxItemBlock>
        <div className="device-register-box-item-card">
          <div className="device-register-box-item-row">
            <div>이름</div>
            <div>{device.name}</div>
          </div>
          <div className="device-register-box-item-row">
            <div>Mac 주소</div>
            <div>{device.macAddress}</div>
          </div>
          <div className="device-register-box-item-row">
            <div>허브 주소</div>
            <div>{device.masterMacAddress}</div>
          </div>
        </div>
        <DeleteBtn onClick={() => setShowModal(true)} />
      </DeviceRegisterBoxItemBlock>
    </>
  );
};

const DeviceRegisterBoxItemBlock = styled.div`
  width: 90%;
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
  &:last-child {
    margin-bottom: 0;
  }

  .device-register-box-item-card {
    width: 90%;
    padding: 1rem;
    border-radius: 8px;
    font-size: 16px;
    background: #fff;
  }

  .device-register-box-item-row {
    display: flex;
    justify-content: space-between;
  }
`;

const DeviceRegisterBox = () => {
  const navigate = useNavigate();
  const { id, floor, space } = useParams();

  const [showInsertModal, setShowInsertModal] = useState(false);
  const [type, setType] = useState(null);

  const { planList } = useSelector(({ device }) => device);
  const result = planList
    .find(plan => plan.floor === floor)
    .space.find(el => el.name.trim() === space);

  return (
    <>
      {showInsertModal && (
        <DeviceInsertModal
          type={type}
          storeId={id}
          spaceId={result.id}
          onClose={() => setShowInsertModal(false)}
        />
      )}
      <RegisterBoxBlock>
        <div className="device-register-space-header">
          <BackArrow
            className="device-register-space-back-icon"
            onClick={() => navigate(-1)}
          />
          <div style={{ marginLeft: 20, fontWeight: 500, fontSize: 18 }}>
            {floor}
          </div>
          <div style={{ marginLeft: 10, fontWeight: 500, fontSize: 18 }}>
            {space}
          </div>
        </div>
        <div className="device-register-space-body">
          <img className="plan-img" src={result?.imgUrl} alt="plan" />
          <div className="deviceList-wrapper">
            <div className="device-list">
              <div className="device-list-header">
                <div className="device-list-title">센서</div>
                <CreateBtn
                  onClick={() => {
                    setType('1');
                    setShowInsertModal(true);
                  }}
                />
              </div>
              {result?.sensor.length === 0 ? (
                <div className="device-list-body-nothing">목록이 없습니다</div>
              ) : (
                result?.sensor.map(device => (
                  <DeviceRegisterBoxItem key={device.id} device={device} />
                ))
              )}
            </div>
            <div className="device-list">
              <div className="device-list-header">
                <div className="device-list-title">컨트롤러</div>
                <CreateBtn
                  onClick={() => {
                    setType('2');
                    setShowInsertModal(true);
                  }}
                />
              </div>
              {result?.controller.length === 0 ? (
                <div className="device-list-body-nothing">목록이 없습니다</div>
              ) : (
                result?.controller.map(device => (
                  <DeviceRegisterBoxItem key={device.id} device={device} />
                ))
              )}
            </div>
          </div>
        </div>
      </RegisterBoxBlock>
    </>
  );
};

export default DeviceRegisterBox;

const RegisterBoxBlock = styled.div`
  margin-top: 1rem;

  .device-register-space-header {
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
  }

  .device-register-space-back-icon {
    margin-left: 1rem;
    font-size: 22px;
  }

  .device-register-space-body {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .plan-img {
    width: 1000px;
  }

  .deviceList-wrapper {
    width: 800px;
    max-height: 400px;
    margin-top: 1rem;
    padding-top: 1rem;
    display: flex;
    justify-content: space-between;
    border: 1px soild rgba(0, 0, 0, 0.1);
    border-radius: 10px;
    background: rgba(0, 0, 0, 0.1);
    overflow-y: auto;
  }

  .device-list {
    width: 100%;
    height: 100%;
    padding-bottom: 1rem;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .device-list-header {
    margin-bottom: 1rem;
    display: flex;
  }

  .device-list-title {
    margin-right: 0.5rem;
    font-size: 1.1rem;
    font-weight: bold;
  }

  .device-list-body-nothing {
    width: 100%;
    margin-top: 1rem;
    font-size: 16px;
    font-weight: 500;
    text-align: center;
  }
`;
